import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Map } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface Item {
  slug: string;
  title: string;
}

const STATIC_LINKS: { path: string; label: string }[] = [
  { path: "/", label: "Home" },
  { path: "/tours", label: "Tours & Excursions" },
  { path: "/destinations", label: "Destinations" },
  { path: "/transfers", label: "Airport Transfers" },
  { path: "/services", label: "Services" },
  { path: "/fleet", label: "Our Fleet" },
  { path: "/about", label: "About Us" },
  { path: "/contact", label: "Contact" },
  { path: "/privacy-policy", label: "Privacy Policy" },
  { path: "/terms", label: "Terms & Conditions" },
  { path: "/cookie-policy", label: "Cookie Policy" },
];

const LinkList = ({ links }: { links: { to: string; label: string }[] }) => (
  <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
    {links.map((l) => (
      <li key={l.to}>
        <Link
          to={l.to}
          className="group flex items-center gap-1.5 py-1.5 text-muted-foreground hover:text-primary transition-colors"
        >
          <ChevronRight className="w-4 h-4 shrink-0 text-primary/50 group-hover:translate-x-0.5 transition-transform" />
          <span className="truncate">{l.label}</span>
        </Link>
      </li>
    ))}
  </ul>
);

const Section = ({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) => (
  <section className="rounded-3xl border border-border bg-card/50 p-6 md:p-8">
    <div className="flex items-baseline justify-between gap-4 mb-5">
      <h2 className="font-display text-2xl font-semibold">{title}</h2>
      <span className="text-sm text-muted-foreground">{count}</span>
    </div>
    {children}
  </section>
);

const SitemapPage = () => {
  const [tours, setTours] = useState<Item[]>([]);
  const [destinations, setDestinations] = useState<Item[]>([]);
  const [routes, setRoutes] = useState<Item[]>([]);

  useEffect(() => {
    document.title = "Sitemap | Allen Travels RD";

    const load = async () => {
      const [t, d, l] = await Promise.all([
        supabase.from("tours").select("slug,title").eq("is_active", true).order("sort_order"),
        supabase.from("destinations").select("slug,title").eq("is_active", true).order("sort_order"),
        supabase
          .from("landing_pages")
          .select("slug,title")
          .eq("is_published", true)
          .order("sort_order"),
      ]);
      setTours((t.data as Item[]) || []);
      setDestinations((d.data as Item[]) || []);
      setRoutes((l.data as Item[]) || []);
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <header className="relative isolate overflow-hidden bg-gradient-to-br from-primary/15 via-background to-background pt-32 pb-14">
        <div className="container mx-auto px-4 relative z-10">
          <div className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary">
            <Map className="w-4 h-4" /> Sitemap
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-4">
            Every page on Allen Travels RD
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            A complete index of our tours, destinations, transfer routes and main pages. Looking for
            the machine-readable version?{" "}
            <a href="/sitemap.xml" className="text-primary underline underline-offset-4">
              sitemap.xml
            </a>
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-24 space-y-6">
        <Section title="Main pages" count={STATIC_LINKS.length}>
          <LinkList links={STATIC_LINKS.map((s) => ({ to: s.path, label: s.label }))} />
        </Section>

        <Section title="Tours & Excursions" count={tours.length}>
          <LinkList links={tours.map((t) => ({ to: `/tours/${t.slug}`, label: t.title }))} />
        </Section>

        <Section title="Destinations" count={destinations.length}>
          <LinkList
            links={destinations.map((d) => ({ to: `/destinations/${d.slug}`, label: d.title }))}
          />
        </Section>

        <Section title="Transfer routes" count={routes.length}>
          <LinkList links={routes.map((r) => ({ to: `/transfers/${r.slug}`, label: r.title }))} />
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default SitemapPage;
